import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconPlay from 'components/icons/IconPlay';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import useVideoMetadata from 'hooks/useVideoMetadata';
import useIsVideoPosterPlayable from 'hooks/useIsVideoPosterPlayable';
import useScrollSelectedIntoView from 'hooks/useScrollSelectedIntoView';
import { GALLERY_IMAGE_LOCATION, GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import { galleryCommonImgixProps } from '../PDPDesktopFullScreenGallery2/galleryCommonImgixProps';
import usePDPDesktopHeroSizeProps from '../PDPDesktopHeroGallery/helpers/usePDPDesktopHeroSizeProps';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from './PDPGalleryRevampCircleButton';
import PDPGalleryRevampLifestyleThumb from './PDPGalleryRevampLifestyleThumb';
const ZOOM_LEVELS = [1, 1.5, 2];

// Expanded lifestyle gallery (CON-8444): 2-col thumb grid + selected photo/video
const PDPGalleryRevampExpandedGallery = t0 => {
  const $ = _c(76);
  const {
    initialIndex: t1,
    initialVideoSelected: t2,
    introVideoId,
    onClose,
    photos: t3,
    productName,
    trackGalleryInteracted,
    viewGalleryImageEvent
  } = t0;
  const initialIndex = t1 === undefined ? 0 : t1;
  const initialVideoSelected = t2 === undefined ? false : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const photos = t4;
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isVideoSelected, setIsVideoSelected] = useState(initialVideoSelected);
  const [zoomIndex, setZoomIndex] = useState(0);
  const thumbGridRef = useRef(null);
  let t5;
  if ($[2] !== introVideoId) {
    t5 = {
      videoId: introVideoId
    };
    $[2] = introVideoId;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    thumbnail: videoThumbnailUrl,
    videoUrl: videoPosterUrl
  } = useVideoMetadata(t5);
  const isPosterPlayable = useIsVideoPosterPlayable(videoPosterUrl);
  const showVideoTile = !!introVideoId && (!!videoThumbnailUrl || isPosterPlayable);
  const t6 = isVideoSelected ? 0 : selectedIndex + (showVideoTile ? 1 : 0);
  let t7;
  if ($[4] !== t6) {
    t7 = {
      axis: "y",
      scrollerRef: thumbGridRef,
      selectedIndex: t6
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  useScrollSelectedIntoView(t7);
  const selectedPhoto = photos[selectedIndex] ?? photos[0];
  const zoom = ZOOM_LEVELS[zoomIndex];
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps(zoom);
  let t8;
  if ($[6] !== photos || $[7] !== viewGalleryImageEvent) {
    t8 = index => {
      setSelectedIndex(index);
      setIsVideoSelected(false);
      setZoomIndex(0);
      const src = photos[index]?.src;
      viewGalleryImageEvent?.({
        imageLocation: GALLERY_IMAGE_LOCATION.LIFESTYLE,
        imageFilename: src?.split("/").pop() || "",
        position: index,
        totalImages: photos.length
      });
    };
    $[6] = photos;
    $[7] = viewGalleryImageEvent;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const selectPhoto = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsVideoSelected(true);
      setZoomIndex(0);
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const selectVideo = t9;
  const itemCount = photos.length + (showVideoTile ? 1 : 0);
  const hasMultipleItems = itemCount > 1;
  let t10;
  if ($[10] !== isVideoSelected || $[11] !== itemCount || $[12] !== photos.length || $[13] !== selectPhoto || $[14] !== selectedIndex || $[15] !== showVideoTile) {
    t10 = offset => {
      if (!showVideoTile) {
        selectPhoto((selectedIndex + offset + photos.length) % photos.length);
        return;
      }
      const currentTile = isVideoSelected ? 0 : selectedIndex + 1;
      const nextTile = (currentTile + offset + itemCount) % itemCount;
      if (nextTile === 0) {
        selectVideo();
      } else {
        selectPhoto(nextTile - 1);
      }
    };
    $[10] = isVideoSelected;
    $[11] = itemCount;
    $[12] = photos.length;
    $[13] = selectPhoto;
    $[14] = selectedIndex;
    $[15] = showVideoTile;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const goToOffset = t10;
  let t11;
  if ($[17] !== goToOffset) {
    t11 = () => goToOffset(-1);
    $[17] = goToOffset;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const goToPrevious = t11;
  let t12;
  if ($[19] !== goToOffset) {
    t12 = () => goToOffset(1);
    $[19] = goToOffset;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const goToNext = t12;
  let t13;
  let t14;
  if ($[21] !== goToNext || $[22] !== goToPrevious || $[23] !== hasMultipleItems || $[24] !== onClose) {
    t13 = () => {
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          onClose();
        } else {
          if (hasMultipleItems && event.key === "ArrowLeft") {
            goToPrevious();
          } else {
            if (hasMultipleItems && event.key === "ArrowRight") {
              goToNext();
            }
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t14 = [hasMultipleItems, goToPrevious, goToNext, onClose];
    $[21] = goToNext;
    $[22] = goToPrevious;
    $[23] = hasMultipleItems;
    $[24] = onClose;
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  useEffect(t13, t14);
  let t15;
  if ($[27] !== isVideoSelected || $[28] !== showVideoTile || $[29] !== videoPosterUrl || $[30] !== videoThumbnailUrl) {
    t15 = showVideoTile && <PDPGalleryRevampLifestyleThumb ariaLabel="Play product video" className="h-auto w-full pt-[72.5%]" imgClassName="absolute inset-0 h-full w-full object-cover" imgSize={{
      width: 120,
      height: 87
    }} src={videoThumbnailUrl} videoUrl={videoPosterUrl} isSelected={isVideoSelected} onClick={selectVideo} icon={<IconPlay width={24} height={24} />} />;
    $[27] = isVideoSelected;
    $[28] = showVideoTile;
    $[29] = videoPosterUrl;
    $[30] = videoThumbnailUrl;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== isVideoSelected || $[33] !== photos || $[34] !== selectPhoto || $[35] !== selectedIndex) {
    let t17;
    if ($[37] !== isVideoSelected || $[38] !== selectPhoto || $[39] !== selectedIndex) {
      t17 = (t18, index_0) => {
        const {
          id,
          src: src_0
        } = t18;
        return <PDPGalleryRevampLifestyleThumb key={`PDPGalleryRevampExpandedThumb${id}`} className="h-auto w-full pt-[72.5%]" imgClassName="absolute inset-0 h-full w-full object-cover" imgSize={{
          width: 120,
          height: 87
        }} src={src_0} isSelected={!isVideoSelected && index_0 === selectedIndex} onClick={() => selectPhoto(index_0)} />;
      };
      $[37] = isVideoSelected;
      $[38] = selectPhoto;
      $[39] = selectedIndex;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    t16 = photos.map(t17);
    $[32] = isVideoSelected;
    $[33] = photos;
    $[34] = selectPhoto;
    $[35] = selectedIndex;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[41] !== t15 || $[42] !== t16) {
    t17 = <div ref={thumbGridRef} className="flex-1 grid grid-cols-2 auto-rows-max gap-1.5 xl:gap-2 2xl:gap-2.5 content-start min-w-[min(250px,28%)] max-w-[28%] h-full overflow-y-auto">{t15}{t16}</div>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== goToNext || $[45] !== goToPrevious || $[46] !== hasMultipleItems || $[47] !== isVideoSelected) {
    t18 = hasMultipleItems && <><button type="button" onClick={goToPrevious} className={classNames("group absolute left-0 top-0 h-full w-16 z-[2] flex items-center justify-center hover:cursor-pointer focus-visible:outline-brand before:absolute before:inset-0 before:rounded-l before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100 before:[background:linear-gradient(90deg,rgba(0,0,0,.35)_.06%,hsla(0,0%,76.9%,0)_99.94%)]", {
        "pointer-events-none before:hidden": isVideoSelected
      })} aria-label="Previous image"><span className="pointer-events-auto relative items-center flex justify-center h-9 w-9 rounded-full bg-white border border-solid border-gray-light6 transition-colors duration-200 group-hover:bg-gray-light9 [&_svg_path]:fill-gray-dark"><IconArrowLeftWhite width={15} height={15} /></span><VisuallyHidden>Previous image</VisuallyHidden></button><button type="button" onClick={goToNext} className={classNames("group absolute right-0 top-0 h-full w-16 z-[2] flex items-center justify-center hover:cursor-pointer focus-visible:outline-brand before:absolute before:inset-0 before:rounded-r before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100 before:[background:linear-gradient(270deg,rgba(0,0,0,.35)_.06%,hsla(0,0%,76.9%,0)_99.94%)]", {
        "pointer-events-none before:hidden": isVideoSelected
      })} aria-label="Next image"><span className="pointer-events-auto absolute left-1/2 -translate-x-1/2 top-[max(50%,11rem)] -translate-y-1/2 items-center flex justify-center h-9 w-9 rounded-full bg-white border border-solid border-gray-light6 transition-colors duration-200 group-hover:bg-gray-light9 [&_svg_path]:fill-gray-dark"><IconArrowRightWhite width={15} height={15} /></span><VisuallyHidden>Next image</VisuallyHidden></button></>;
    $[44] = goToNext;
    $[45] = goToPrevious;
    $[46] = hasMultipleItems;
    $[47] = isVideoSelected;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== introVideoId || $[50] !== isVideoSelected || $[51] !== mainPhotoSizeProps || $[52] !== productName || $[53] !== selectedPhoto || $[54] !== showVideoTile || $[55] !== zoom) {
    t19 = isVideoSelected && showVideoTile ? <div className="h-full w-full"><PDPProductIntroVideo videoId={introVideoId} /></div> : !!selectedPhoto?.src && <div style={{
      transform: `scale(${zoom})`
    }} className="h-full w-full origin-center transition-transform duration-200 ease-out"><JBProductImage className="h-full w-full object-contain select-none" alt={productName ? String(productName) : ""} {...getMainImageImgixPropsForPhoto(selectedPhoto, false)} crops={{}} {...galleryCommonImgixProps} {...mainPhotoSizeProps} lazy={false} priority={true} /></div>;
    $[49] = introVideoId;
    $[50] = isVideoSelected;
    $[51] = mainPhotoSizeProps;
    $[52] = productName;
    $[53] = selectedPhoto;
    $[54] = showVideoTile;
    $[55] = zoom;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t19) {
    t20 = <div className="h-full overflow-hidden flex rounded">{t19}</div>;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t18 || $[60] !== t20) {
    t21 = <div className="relative h-full aspect-[3/2] min-w-0">{t18}{t20}</div>;
    $[59] = t18;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <IconClose width={9} height={9} />;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== onClose) {
    t23 = <PDPGalleryRevampCircleButton ariaLabel="Close gallery" onClick={onClose}>{t22}</PDPGalleryRevampCircleButton>;
    $[63] = onClose;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== isVideoSelected || $[66] !== trackGalleryInteracted || $[67] !== zoomIndex) {
    t24 = !isVideoSelected && <><PDPGalleryRevampCircleButton ariaLabel="Zoom in" disabled={zoomIndex >= ZOOM_LEVELS.length - 1} onClick={() => {
        setZoomIndex(_temp);
        trackGalleryInteracted?.(GALLERY_INTERACTION.ZOOMED_INTERACTED);
      }}><IconPlus width={15} height={15} /></PDPGalleryRevampCircleButton><PDPGalleryRevampCircleButton ariaLabel="Zoom out" disabled={zoomIndex <= 0} onClick={() => {
        setZoomIndex(_temp2);
        trackGalleryInteracted?.(GALLERY_INTERACTION.ZOOMED_INTERACTED);
      }}><IconMinus width={15} height={15} /></PDPGalleryRevampCircleButton></>;
    $[65] = isVideoSelected;
    $[66] = trackGalleryInteracted;
    $[67] = zoomIndex;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t23 || $[70] !== t24) {
    t25 = <div className="absolute flex flex-col gap-2.5 right-6 top-6 z-[3]">{t23}{t24}</div>;
    $[69] = t23;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== t17 || $[73] !== t21 || $[74] !== t25) {
    t26 = <div className="relative flex gap-3 h-full p-3" data-xc="PDPGalleryRevampExpandedGallery">{t17}{t21}{t25}</div>;
    $[72] = t17;
    $[73] = t21;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  return t26;
};
PDPGalleryRevampExpandedGallery.propTypes = {
  initialIndex: PropTypes.number,
  initialVideoSelected: PropTypes.bool,
  introVideoId: PropTypes.string,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  productName: PropTypes.string,
  trackGalleryInteracted: PropTypes.func,
  viewGalleryImageEvent: PropTypes.func
};
export default PDPGalleryRevampExpandedGallery;
function _temp(prev) {
  return Math.min(prev + 1, ZOOM_LEVELS.length - 1);
}
function _temp2(prev_0) {
  return Math.max(prev_0 - 1, 0);
}